import { translate, type WashedLocale } from '@washed/i18n';
import { PageShell } from './PageShell.js';
import styles from './SeoIntentPage.module.css';

const SITE = 'https://washedafrica.com';

export type SeoIntentKey =
  | 'washerwoman'
  | 'home-laundry'
  | 'washerwoman-price'
  | 'trusted-washerwoman'
  | 'pressing-vs-washerwoman'
  | 'laundry-subscription';

type Section = {
  heading: string;
  paragraphs: string[];
};

type Faq = {
  q: string;
  a: string;
};

type IntentCopy = {
  slug: string;
  eyebrow: string;
  title: string;
  intro: string;
  serviceType: string;
  sections: Section[];
  faqs: Faq[];
  related: SeoIntentKey[];
};

const PATH_TO_INTENT: Record<string, SeoIntentKey> = {
  '/laveuse-a-domicile-lome': 'washerwoman',
  '/washerwoman-service-lome': 'washerwoman',
  '/lessive-a-domicile-lome': 'home-laundry',
  '/in-home-laundry-lome': 'home-laundry',
  '/prix-laveuse-lome': 'washerwoman-price',
  '/laundry-prices-lome': 'washerwoman-price',
  '/trouver-laveuse-fiable-lome': 'trusted-washerwoman',
  '/reliable-washerwoman-lome': 'trusted-washerwoman',
  '/pressing-ou-laveuse': 'pressing-vs-washerwoman',
  '/dry-cleaning-vs-home-laundry-lome': 'pressing-vs-washerwoman',
  '/abonnement-lessive-lome': 'laundry-subscription',
  '/laundry-subscription-lome': 'laundry-subscription',
};

export function seoIntentKeyFromPath(path: string): SeoIntentKey | null {
  const normalized = path.replace(/\/+$/u, '') || '/';
  return PATH_TO_INTENT[normalized] ?? null;
}

// Service coverage comes from the copy deck (marketing.seo.coverage.*): every
// Lomé quartier is open at beta launch (#566).
const COPY: Record<SeoIntentKey, Record<WashedLocale, IntentCopy>> = {
  washerwoman: {
    fr: {
      slug: 'laveuse-a-domicile-lome',
      eyebrow: 'Washed · Laveuse à domicile',
      title: 'Laveuse à domicile à Lomé',
      intro:
        'Washed organise une relation régulière avec une laveuse formée et vérifiée qui vient chez vous, à Lomé, pour laver votre linge sur place. Votre linge ne part pas au pressing : la prestation se fait à domicile, avec votre eau et votre savon.',
      serviceType: 'Laveuse à domicile à Lomé',
      sections: [
        {
          heading: 'Ce que signifie « laveuse à domicile » chez Washed',
          paragraphs: [
            'Washed n’est ni une laverie ni un service de collecte. Une laveuse vient au domicile du foyer et lave le linge sur place. Le foyer fournit l’eau et le savon ; Washed organise la mise en relation, le planning et le paiement.',
            'Le service fonctionne sur abonnement mensuel, avec une ou deux visites selon la formule choisie. L’objectif est d’éviter de devoir rechercher une nouvelle personne à chaque lessive.',
          ],
        },
        {
          heading: 'Une relation régulière, pas une mission isolée',
          paragraphs: [
            'La laveuse habituelle est réassignée par défaut aux visites du même foyer afin de construire une relation de confiance dans la durée. Si elle est indisponible ou qu’un changement est nécessaire, Washed peut organiser un remplacement.',
            'Cette continuité est particulièrement utile aux foyers qui veulent déléguer une tâche récurrente sans recruter une aide ménagère à temps plein ni envoyer leurs vêtements ailleurs.',
          ],
        },
        {
          heading: 'Tarifs et quartiers desservis',
          paragraphs: [
            'Les formules Solo commencent à 4 500 XOF par mois pour une visite et 8 500 XOF par mois pour deux visites. Des formules Duo et Foyer couvrent aussi les foyers de 2 à 5 personnes.',
            translate('marketing.seo.coverage.body', 'fr'),
          ],
        },
      ],
      faqs: [
        {
          q: 'Quelle est la différence entre une laveuse à domicile et un pressing ?',
          a: 'Avec Washed, le linge reste chez vous et est lavé sur place. Un pressing prend généralement le linge en charge hors du domicile, puis le rend après traitement.',
        },
        {
          q: 'Qui fournit l’eau et le savon ?',
          a: 'Le foyer fournit son eau et son savon, comme pour une lessive habituelle à la maison.',
        },
        {
          q: 'Est-ce toujours la même laveuse ?',
          a: 'Washed réassigne votre laveuse habituelle par défaut. Un remplacement peut être organisé lorsqu’elle est indisponible ou lorsqu’un changement est nécessaire.',
        },
        {
          q: 'Combien coûte une laveuse Washed à Lomé ?',
          a: 'Les formules Solo commencent à 4 500 XOF par mois pour une visite et 8 500 XOF par mois pour deux visites.',
        },
      ],
      related: ['washerwoman-price', 'trusted-washerwoman', 'laundry-subscription'],
    },
    en: {
      slug: 'washerwoman-service-lome',
      eyebrow: 'Washed · In-home washerwoman',
      title: 'Washerwoman service in Lomé',
      intro:
        'Washed matches households in Lomé with a trained and vetted washerwoman who comes to the home and washes laundry on-site. Your clothes are not taken to a laundromat or dry cleaner: the service happens at home using your water and soap.',
      serviceType: 'In-home washerwoman service in Lomé',
      sections: [
        {
          heading: 'What an in-home washerwoman means with Washed',
          paragraphs: [
            'Washed is not a laundromat or pickup-and-delivery service. The washerwoman comes to the household and washes on-site. The household provides water and soap while Washed coordinates the match, schedule and payment.',
            'The service is subscription-based, with one or two visits per month depending on the plan, so households do not need to search for a new person every time laundry piles up.',
          ],
        },
        {
          heading: 'A recurring relationship instead of one-off help',
          paragraphs: [
            'Your regular washerwoman is assigned to your visits by default so trust can build over time. If she becomes unavailable or a change is needed, Washed can arrange a replacement.',
            'This model is designed for households that want recurring laundry help without hiring full-time domestic help or sending clothes away.',
          ],
        },
        {
          heading: 'Pricing and coverage in Lomé',
          paragraphs: [
            'Solo plans start at 4,500 XOF per month for one visit and 8,500 XOF per month for two visits. Duo and Household plans also cover homes with 2 to 5 people.',
            translate('marketing.seo.coverage.body', 'en'),
          ],
        },
      ],
      faqs: [
        {
          q: 'How is a washerwoman service different from a dry cleaner?',
          a: 'With Washed, laundry stays at your home and is washed on-site. A dry cleaner or pressing normally takes the items off-site and returns them later.',
        },
        {
          q: 'Who provides the water and soap?',
          a: 'The household provides its own water and soap, just as it would for normal laundry at home.',
        },
        {
          q: 'Do I get the same washerwoman each month?',
          a: 'Your regular washerwoman is reassigned by default. Washed can arrange a replacement when she is unavailable or a change is needed.',
        },
        {
          q: 'How much does Washed cost in Lomé?',
          a: 'Solo plans start at 4,500 XOF per month for one visit and 8,500 XOF per month for two visits.',
        },
      ],
      related: ['washerwoman-price', 'trusted-washerwoman', 'laundry-subscription'],
    },
  },
  'home-laundry': {
    fr: {
      slug: 'lessive-a-domicile-lome',
      eyebrow: 'Washed · Lessive à domicile',
      title: 'Service de lessive à domicile à Lomé',
      intro:
        'Pour les foyers qui cherchent quelqu’un pour faire la lessive sans emporter les vêtements, Washed organise des visites régulières d’une laveuse à domicile à Lomé.',
      serviceType: 'Service de lessive à domicile à Lomé',
      sections: [
        {
          heading: 'La lessive reste chez vous',
          paragraphs: [
            'La laveuse Washed lave vos vêtements au domicile. Vous gardez vos produits, votre eau et votre linge chez vous pendant toute la prestation.',
            'Ce fonctionnement est différent des services de collecte-livraison : Washed organise une personne qui vient faire la lessive sur place plutôt que transporter le linge vers un autre site.',
          ],
        },
        {
          heading: 'Pensé pour une tâche qui revient chaque semaine',
          paragraphs: [
            'La lessive n’est pas un besoin ponctuel. Washed la traite comme une routine à organiser : abonnement mensuel, jour planifié et laveuse habituelle réassignée par défaut.',
            'Les foyers peuvent choisir une formule à une visite par mois ou à deux visites par mois selon leur volume et leur rythme.',
          ],
        },
        {
          heading: 'Pour qui ?',
          paragraphs: [
            'Le service vise notamment les personnes très occupées, les familles et les foyers qui préfèrent déléguer la lessive sans engager une aide ménagère pour l’ensemble des tâches domestiques.',
            translate('marketing.seo.coverage.body', 'fr'),
          ],
        },
      ],
      faqs: [
        {
          q: 'Washed vient-il récupérer le linge ?',
          a: 'Non. La laveuse vient chez vous et lave le linge sur place ; le linge ne quitte pas le domicile.',
        },
        {
          q: 'Combien de fois par mois la laveuse peut-elle venir ?',
          a: 'Les formules individuelles prévoient une ou deux visites par mois selon l’abonnement choisi.',
        },
        {
          q: 'Dois-je acheter un savon particulier ?',
          a: 'Non. Vous utilisez votre eau et votre savon habituels.',
        },
        {
          q: 'Quels quartiers de Lomé Washed sert-il ?',
          a: translate('marketing.seo.coverage.faq.answer', 'fr'),
        },
      ],
      related: ['washerwoman', 'pressing-vs-washerwoman', 'laundry-subscription'],
    },
    en: {
      slug: 'in-home-laundry-lome',
      eyebrow: 'Washed · In-home laundry',
      title: 'In-home laundry service in Lomé',
      intro:
        'For households looking for someone to do the laundry without taking clothes away, Washed organizes recurring in-home laundry visits in Lomé.',
      serviceType: 'In-home laundry service in Lomé',
      sections: [
        {
          heading: 'Your laundry stays at home',
          paragraphs: [
            'A Washed washerwoman washes your clothes at your home. Your laundry, water and products remain with you throughout the visit.',
            'That is different from pickup-and-delivery laundry. Washed organizes a person to come and do the washing on-site rather than transporting your clothes elsewhere.',
          ],
        },
        {
          heading: 'Built around a recurring household chore',
          paragraphs: [
            'Laundry is not a one-time need. Washed treats it as a routine to organize: a monthly plan, scheduled visits and the regular washerwoman reassigned by default.',
            'Households can choose one or two visits per month depending on their plan and laundry rhythm.',
          ],
        },
        {
          heading: 'Who is it for?',
          paragraphs: [
            'The service is designed for busy people, families and households that want to delegate laundry without hiring domestic help for every household task.',
            translate('marketing.seo.coverage.body', 'en'),
          ],
        },
      ],
      faqs: [
        {
          q: 'Does Washed pick up the laundry?',
          a: 'No. The washerwoman comes to your home and washes on-site; the laundry does not leave your home.',
        },
        {
          q: 'How often can the washerwoman come?',
          a: 'Individual plans provide one or two visits per month depending on the subscription.',
        },
        {
          q: 'Do I need special detergent?',
          a: 'No. You use the water and soap you normally use at home.',
        },
        {
          q: 'Which neighbourhoods of Lomé does Washed serve?',
          a: translate('marketing.seo.coverage.faq.answer', 'en'),
        },
      ],
      related: ['washerwoman', 'pressing-vs-washerwoman', 'laundry-subscription'],
    },
  },
  'washerwoman-price': {
    fr: {
      slug: 'prix-laveuse-lome',
      eyebrow: 'Washed · Prix',
      title: 'Prix d’une laveuse à Lomé : les tarifs Washed',
      intro:
        'Washed affiche six abonnements mensuels clairs pour la lessive à domicile à Lomé : Solo 4 500 / 8 500 XOF, Duo 6 000 / 11 500 XOF et Foyer 9 500 / 18 000 XOF.',
      serviceType: 'Tarifs de laveuse à domicile à Lomé',
      sections: [
        {
          heading: 'Les six formules',
          paragraphs: [
            'Solo coûte 4 500 XOF par mois pour une visite ou 8 500 XOF pour deux. Duo coûte 6 000 ou 11 500 XOF. Foyer, pour 3 à 5 personnes, coûte 9 500 ou 18 000 XOF selon la fréquence.',
            'Chaque visite couvre la quantité raisonnable de linge du foyer enregistré pour la période de service. Un volume clairement excessif est signalé pour examen. Le paiement se fait par mobile money via Mixx by Yas ou Flooz.',
          ],
        },
        {
          heading: 'Ce que le prix organise',
          paragraphs: [
            'Le prix ne correspond pas seulement à une mission isolée : Washed organise la mise en relation, le planning, le paiement et la continuité avec la laveuse habituelle lorsque cela est possible.',
            'Le foyer fournit l’eau et le savon. Cette organisation permet de garder le modèle simple et centré sur le travail de la laveuse à domicile.',
          ],
        },
        {
          heading: 'Comparer correctement les options',
          paragraphs: [
            'Un pressing, une aide ménagère et une laveuse à domicile ne facturent pas forcément la même chose parce qu’ils ne rendent pas exactement le même service. Il faut comparer le lieu de lavage, la fréquence, le transport du linge, les autres tâches incluses et la façon dont la relation est organisée.',
            'Washed publie ses formules afin que le coût mensuel soit connu avant l’inscription.',
          ],
        },
      ],
      faqs: [
        {
          q: 'Quel est le prix minimum de Washed ?',
          a: 'La formule Solo 1× commence à 4 500 XOF par mois pour une visite.',
        },
        {
          q: 'Combien coûte deux visites par mois ?',
          a: 'La formule Solo 2× coûte 8 500 XOF par mois pour deux visites.',
        },
        {
          q: 'Existe-t-il un tarif famille ?',
          a: 'Oui. Duo est à 6 000 / 11 500 XOF par mois pour 2 personnes ; Foyer est à 9 500 / 18 000 XOF pour 3 à 5 personnes.',
        },
        {
          q: 'Comment paie-t-on ?',
          a: 'Le paiement se fait par mobile money via Mixx by Yas ou Flooz, avant chaque cycle de visites : jamais après le service.',
        },
      ],
      related: ['washerwoman', 'laundry-subscription', 'pressing-vs-washerwoman'],
    },
    en: {
      slug: 'laundry-prices-lome',
      eyebrow: 'Washed · Pricing',
      title: 'Laundry prices in Lomé: Washed plans',
      intro:
        'Washed publishes six clear monthly prices for in-home laundry in Lomé: Solo 4,500 / 8,500 XOF, Duo 6,000 / 11,500 XOF, and Household 9,500 / 18,000 XOF.',
      serviceType: 'In-home laundry pricing in Lomé',
      sections: [
        {
          heading: 'The six plans',
          paragraphs: [
            'Solo costs 4,500 XOF per month for one visit or 8,500 XOF for two. Duo costs 6,000 or 11,500 XOF. Household, for 3 to 5 people, costs 9,500 or 18,000 XOF depending on frequency.',
            'Each visit covers a reasonable amount of laundry for the registered household and service period. Clearly excessive volume is flagged for review. Payment is by mobile money through Mixx by Yas or Flooz.',
          ],
        },
        {
          heading: 'What the price organizes',
          paragraphs: [
            'The price is not only for a one-off task. Washed organizes the match, schedule, payment and continuity with the household’s regular washerwoman when possible.',
            'The household provides water and soap, keeping the model simple and focused on the washerwoman’s work in the home.',
          ],
        },
        {
          heading: 'How to compare laundry options',
          paragraphs: [
            'A dry cleaner, domestic helper and in-home washerwoman may charge differently because they do not provide the same service. Compare where the washing happens, frequency, clothing transport, other chores included and how the worker relationship is managed.',
            'Washed publishes its plans so the monthly cost is known before signup.',
          ],
        },
      ],
      faqs: [
        {
          q: 'What is the cheapest Washed plan?',
          a: 'Solo 1× starts at 4,500 XOF per month for one visit.',
        },
        {
          q: 'How much are two visits per month?',
          a: 'Solo 2× costs 8,500 XOF per month for two visits.',
        },
        {
          q: 'Is there a family price?',
          a: 'Yes. Duo is 6,000 / 11,500 XOF per month for 2 people; Household is 9,500 / 18,000 XOF for 3 to 5 people.',
        },
        {
          q: 'How do I pay?',
          a: 'Payment is by mobile money through Mixx by Yas or Flooz, before each cycle of visits: never after the service.',
        },
      ],
      related: ['washerwoman', 'laundry-subscription', 'pressing-vs-washerwoman'],
    },
  },
  'trusted-washerwoman': {
    fr: {
      slug: 'trouver-laveuse-fiable-lome',
      eyebrow: 'Washed · Confiance',
      title: 'Comment trouver une laveuse fiable à Lomé',
      intro:
        'Pour une tâche réalisée à l’intérieur du domicile, la fiabilité compte autant que le prix. Washed structure la relation autour de laveuses formées et vérifiées, d’un planning organisé et d’une continuité avec la même personne lorsque cela est possible.',
      serviceType: 'Laveuse fiable et vérifiée à Lomé',
      sections: [
        {
          heading: 'Les critères à vérifier',
          paragraphs: [
            'Avant de confier une lessive régulière à quelqu’un, il est utile de savoir qui intervient, comment les rendez-vous sont organisés, comment le paiement est géré et ce qui se passe lorsque la personne habituelle est indisponible.',
            'Washed centralise ces éléments dans un même service plutôt que de laisser chaque foyer reconstruire son propre système de bouche-à-oreille.',
          ],
        },
        {
          heading: 'Pourquoi la continuité réduit les frictions',
          paragraphs: [
            'Une relation récurrente permet à la laveuse de connaître progressivement les habitudes du foyer et évite de repartir de zéro à chaque visite. C’est pour cette raison que la laveuse habituelle est réassignée par défaut.',
            'Lorsque cette personne n’est pas disponible, Washed peut organiser un remplacement au lieu de laisser le foyer rechercher seul une nouvelle laveuse.',
          ],
        },
        {
          heading: 'Fiabilité ne veut pas dire promesse irréaliste',
          paragraphs: [
            'Aucun service humain ne peut garantir qu’une personne ne sera jamais absente. Une organisation fiable prévoit donc aussi les exceptions : indisponibilité, changement de planning et remplacement.',
            'Le but de Washed est de rendre la relation plus prévisible et plus facile à gérer pour le foyer comme pour la laveuse.',
          ],
        },
      ],
      faqs: [
        {
          q: 'Les laveuses Washed sont-elles vérifiées ?',
          a: 'Oui. Washed présente ses laveuses comme formées et vérifiées avant leur affectation aux foyers.',
        },
        {
          q: 'Puis-je garder la même laveuse ?',
          a: 'La laveuse habituelle est réassignée par défaut afin de favoriser une relation régulière.',
        },
        {
          q: 'Que se passe-t-il si elle est indisponible ?',
          a: 'Washed peut organiser un remplacement lorsqu’une indisponibilité ou un changement rend la visite habituelle impossible.',
        },
        {
          q: 'Le paiement se fait-il directement à la laveuse ?',
          a: 'Non. Le paiement de l’abonnement est organisé par Washed via mobile money.',
        },
      ],
      related: ['washerwoman', 'washerwoman-price', 'laundry-subscription'],
    },
    en: {
      slug: 'reliable-washerwoman-lome',
      eyebrow: 'Washed · Trust',
      title: 'How to find a reliable washerwoman in Lomé',
      intro:
        'For work done inside the home, reliability matters as much as price. Washed structures the relationship around trained and vetted washerwomen, organized scheduling and continuity with the same person when possible.',
      serviceType: 'Reliable vetted washerwoman in Lomé',
      sections: [
        {
          heading: 'What to check before choosing someone',
          paragraphs: [
            'Before relying on someone for recurring laundry, it helps to know who is coming, how visits are scheduled, how payment is handled and what happens when the regular person is unavailable.',
            'Washed brings those elements into one service instead of making every household rebuild the process through informal referrals.',
          ],
        },
        {
          heading: 'Why continuity reduces friction',
          paragraphs: [
            'A recurring relationship lets the washerwoman learn the household’s routine over time and avoids starting from zero at every visit. That is why the regular washerwoman is reassigned by default.',
            'If she is unavailable, Washed can arrange a replacement rather than leaving the household to find someone new on its own.',
          ],
        },
        {
          heading: 'Reliability is not an unrealistic promise',
          paragraphs: [
            'No human service can guarantee that a worker will never be absent. A reliable system therefore needs a way to handle exceptions such as unavailability, schedule changes and replacements.',
            'Washed aims to make the relationship more predictable and easier to manage for both the household and the washerwoman.',
          ],
        },
      ],
      faqs: [
        {
          q: 'Are Washed washerwomen vetted?',
          a: 'Yes. Washed describes its washerwomen as trained and vetted before assignment to households.',
        },
        {
          q: 'Can I keep the same washerwoman?',
          a: 'The regular washerwoman is reassigned by default to support a recurring relationship.',
        },
        {
          q: 'What happens if she is unavailable?',
          a: 'Washed can arrange a replacement when unavailability or a schedule change prevents the usual visit.',
        },
        {
          q: 'Do I pay the washerwoman directly?',
          a: 'No. The subscription payment is organized through Washed using mobile money.',
        },
      ],
      related: ['washerwoman', 'washerwoman-price', 'laundry-subscription'],
    },
  },
  'pressing-vs-washerwoman': {
    fr: {
      slug: 'pressing-ou-laveuse',
      eyebrow: 'Washed · Comparatif',
      title: 'Pressing ou laveuse à domicile : quelle option à Lomé ?',
      intro:
        'Un pressing et une laveuse à domicile répondent au même problème général — avoir du linge propre — mais avec des modèles très différents. Le bon choix dépend surtout du type de linge, de la fréquence et du fait que vous vouliez ou non que vos vêtements quittent le domicile.',
      serviceType: 'Alternative au pressing à Lomé',
      sections: [
        {
          heading: 'Quand le pressing a du sens',
          paragraphs: [
            'Le pressing est adapté lorsqu’on veut confier des vêtements à un établissement extérieur, notamment pour des traitements ou finitions spécifiques proposés par ce professionnel.',
            'Selon le service, il peut aussi inclure collecte, livraison, repassage ou traitement de pièces particulières. Il faut donc comparer les prestations réellement incluses plutôt que le seul prix affiché.',
          ],
        },
        {
          heading: 'Quand une laveuse à domicile a du sens',
          paragraphs: [
            'Une laveuse à domicile répond mieux au besoin de lessive courante lorsque le foyer souhaite que le linge reste à la maison. Avec Washed, la laveuse vient sur place et utilise l’eau et le savon du foyer.',
            'Le modèle est récurrent : une ou deux visites par mois et, par défaut, la même laveuse réassignée au foyer.',
          ],
        },
        {
          heading: 'La vraie comparaison',
          paragraphs: [
            'Posez cinq questions : où le linge est-il lavé, qui le transporte, à quelle fréquence ai-je besoin du service, ai-je besoin d’autres traitements, et est-ce que je préfère une relation régulière avec la même personne ?',
            'Washed n’essaie pas de remplacer chaque usage du pressing. Il crée une alternative spécifique pour la lessive récurrente à domicile.',
          ],
        },
      ],
      faqs: [
        {
          q: 'Washed est-il un pressing ?',
          a: 'Non. Washed organise une laveuse qui vient laver le linge chez vous ; le linge n’est pas emporté dans un établissement.',
        },
        {
          q: 'Une laveuse à domicile est-elle toujours moins chère ?',
          a: 'Pas nécessairement dans tous les cas, car les prestations sont différentes. Il faut comparer la fréquence, le type de linge et les services inclus.',
        },
        {
          q: 'Puis-je utiliser Washed pour ma lessive régulière ?',
          a: 'Oui. Le modèle Washed est justement conçu autour d’un abonnement mensuel avec une ou deux visites.',
        },
        {
          q: 'Mon linge quitte-t-il la maison ?',
          a: 'Non. Avec Washed, la lessive est faite sur place au domicile.',
        },
      ],
      related: ['home-laundry', 'washerwoman', 'washerwoman-price'],
    },
    en: {
      slug: 'dry-cleaning-vs-home-laundry-lome',
      eyebrow: 'Washed · Comparison',
      title: 'Dry cleaning or in-home laundry in Lomé?',
      intro:
        'A pressing/dry cleaner and an in-home washerwoman solve the same broad problem — clean clothes — through very different models. The right choice depends on garment type, frequency and whether you want your clothes to leave the home.',
      serviceType: 'Alternative to dry cleaning in Lomé',
      sections: [
        {
          heading: 'When a pressing or dry cleaner makes sense',
          paragraphs: [
            'A pressing is useful when you want garments handled at an external facility, including specialist treatments or finishes offered by that business.',
            'Depending on the provider, the service may include pickup, delivery, ironing or treatment of particular items. Compare what is actually included rather than only the headline price.',
          ],
        },
        {
          heading: 'When in-home laundry makes sense',
          paragraphs: [
            'An in-home washerwoman is better suited to recurring everyday laundry when the household wants clothes to stay at home. With Washed, the washerwoman comes to the home and uses the household’s water and soap.',
            'The model is recurring: one or two visits per month, with the regular washerwoman reassigned by default.',
          ],
        },
        {
          heading: 'The comparison that matters',
          paragraphs: [
            'Ask five questions: where is the laundry washed, who transports it, how often do I need help, do I need specialist garment treatment, and do I prefer a recurring relationship with the same person?',
            'Washed is not intended to replace every dry-cleaning use case. It is a specific alternative for recurring household laundry done at home.',
          ],
        },
      ],
      faqs: [
        {
          q: 'Is Washed a dry cleaner?',
          a: 'No. Washed organizes a washerwoman who comes to your home and washes on-site; the laundry is not taken to a facility.',
        },
        {
          q: 'Is in-home laundry always cheaper?',
          a: 'Not necessarily in every case because the services differ. Compare frequency, garment type and what each service includes.',
        },
        {
          q: 'Can I use Washed for recurring laundry?',
          a: 'Yes. Washed is designed around a monthly subscription with one or two visits.',
        },
        {
          q: 'Do my clothes leave the house?',
          a: 'No. With Washed, laundry is done on-site at the home.',
        },
      ],
      related: ['home-laundry', 'washerwoman', 'washerwoman-price'],
    },
  },
  'laundry-subscription': {
    fr: {
      slug: 'abonnement-lessive-lome',
      eyebrow: 'Washed · Abonnement',
      title: 'Abonnement lessive à Lomé',
      intro:
        'Washed transforme la lessive à domicile en service récurrent : un abonnement mensuel, une ou deux visites planifiées et une laveuse habituelle réassignée par défaut au même foyer.',
      serviceType: 'Abonnement mensuel de lessive à domicile à Lomé',
      sections: [
        {
          heading: 'Pourquoi un abonnement ?',
          paragraphs: [
            'La lessive revient chaque semaine. Chercher une personne, renégocier le prix et organiser un nouveau rendez-vous à chaque fois ajoute une deuxième tâche à la tâche elle-même.',
            'L’abonnement Washed transforme ce besoin répétitif en routine planifiée avec un prix mensuel connu.',
          ],
        },
        {
          heading: 'Une ou deux visites par mois',
          paragraphs: [
            'Solo 1× prévoit une visite par mois à 4 500 XOF. Solo 2× prévoit deux visites par mois à 8 500 XOF. Des formules Duo et Foyer existent pour les foyers de 2 à 5 personnes.',
            'Chaque visite est prévue pour la quantité raisonnable de linge du foyer enregistré correspondant à la période de service ; un volume clairement excessif est signalé pour examen.',
          ],
        },
        {
          heading: 'Souplesse et continuité',
          paragraphs: [
            'L’abonnement peut être annulé sans frais et peut être mis en pause gratuitement jusqu’à deux mois par an selon les conditions actuellement publiées par Washed.',
            'La laveuse habituelle est réassignée par défaut, ce qui permet de garder la dimension humaine d’une relation régulière même lorsque le planning et le paiement sont organisés par la plateforme.',
          ],
        },
      ],
      faqs: [
        {
          q: 'Combien coûte l’abonnement lessive ?',
          a: 'Les formules Solo sont à 4 500 XOF par mois pour une visite et 8 500 XOF pour deux visites.',
        },
        {
          q: 'Puis-je arrêter l’abonnement ?',
          a: 'Oui. Les conditions actuellement publiées par Washed indiquent une annulation libre à tout moment sans frais.',
        },
        {
          q: 'Puis-je mettre l’abonnement en pause ?',
          a: 'Oui. Washed indique une pause gratuite jusqu’à deux mois par an.',
        },
        {
          q: 'Est-ce toujours la même laveuse ?',
          a: 'La laveuse habituelle est réassignée par défaut, avec possibilité de remplacement lorsque cela est nécessaire.',
        },
      ],
      related: ['washerwoman-price', 'washerwoman', 'home-laundry'],
    },
    en: {
      slug: 'laundry-subscription-lome',
      eyebrow: 'Washed · Subscription',
      title: 'Monthly laundry subscription in Lomé',
      intro:
        'Washed turns in-home laundry into a recurring service: a monthly subscription, one or two scheduled visits and the household’s regular washerwoman reassigned by default.',
      serviceType: 'Monthly in-home laundry subscription in Lomé',
      sections: [
        {
          heading: 'Why a subscription?',
          paragraphs: [
            'Laundry comes back every week. Finding someone, renegotiating a price and organizing a new appointment every time turns the chore into an additional coordination problem.',
            'A Washed subscription turns that recurring need into a planned routine with a known monthly price.',
          ],
        },
        {
          heading: 'One or two visits per month',
          paragraphs: [
            'Solo 1× provides one visit per month for 4,500 XOF. Solo 2× provides two visits per month for 8,500 XOF. Duo and Household plans cover homes with 2 to 5 people.',
            'Each visit is designed for a reasonable amount of laundry for the registered household and service period; clearly excessive volume is flagged for review.',
          ],
        },
        {
          heading: 'Flexibility and continuity',
          paragraphs: [
            'The subscription can be cancelled without a fee and paused free of charge for up to two months per year under Washed’s currently published terms.',
            'The regular washerwoman is reassigned by default, preserving the human continuity of a recurring relationship while scheduling and payment are coordinated through the platform.',
          ],
        },
      ],
      faqs: [
        {
          q: 'How much is the laundry subscription?',
          a: 'Solo plans cost 4,500 XOF per month for one visit and 8,500 XOF for two visits.',
        },
        {
          q: 'Can I cancel the subscription?',
          a: 'Yes. Washed’s currently published terms state that you can cancel at any time without a fee.',
        },
        {
          q: 'Can I pause the subscription?',
          a: 'Yes. Washed states that the subscription can be paused free of charge for up to two months per year.',
        },
        {
          q: 'Do I keep the same washerwoman?',
          a: 'The regular washerwoman is reassigned by default, with a replacement available when needed.',
        },
      ],
      related: ['washerwoman-price', 'washerwoman', 'home-laundry'],
    },
  },
};

interface SeoIntentPageProps {
  intent: SeoIntentKey;
  locale: WashedLocale;
  onLocaleChange: (locale: WashedLocale) => void;
}

function pageHref(intent: SeoIntentKey, locale: WashedLocale): string {
  const slug = COPY[intent][locale].slug;
  return locale === 'en' ? `/en/${slug}` : `/${slug}`;
}

export function SeoIntentPage({ intent, locale, onLocaleChange }: SeoIntentPageProps) {
  const c = COPY[intent][locale];
  const href = pageHref(intent, locale);
  const pageUrl = `${SITE}${href}`;
  const facts =
    locale === 'fr'
      ? [
          ['Lieu', 'Chez vous, à Lomé'],
          ['Rythme', '1 ou 2 visites par mois'],
          ['Prix', 'À partir de 4 500 XOF/mois'],
          ['Produits', 'Votre eau et votre savon'],
        ]
      : [
          ['Where', 'At your home in Lomé'],
          ['Frequency', '1 or 2 visits per month'],
          ['Price', 'From 4,500 XOF/month'],
          ['Supplies', 'Your water and soap'],
        ];

  const structured = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: c.title,
        description: c.intro,
        inLanguage: locale,
        isPartOf: { '@id': `${SITE}/#website` },
        about: { '@id': `${SITE}/#organization` },
      },
      {
        '@type': 'Service',
        '@id': `${pageUrl}#service`,
        name: c.serviceType,
        serviceType: c.serviceType,
        provider: { '@id': `${SITE}/#organization` },
        areaServed: {
          '@type': 'City',
          name: 'Lomé',
          containedInPlace: { '@type': 'Country', name: 'Togo' },
        },
        url: pageUrl,
        description: c.intro,
      },
    ],
  };

  return (
    <PageShell
      locale={locale}
      onLocaleChange={onLocaleChange}
      eyebrow={c.eyebrow}
      title={c.title}
      intro={c.intro}
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structured) }}
      />

      <section className={styles.facts} aria-label={locale === 'fr' ? 'En bref' : 'At a glance'}>
        {facts.map(([label, value]) => (
          <div key={label} className={styles.fact}>
            <span className={styles.factLabel}>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </section>

      <div className={styles.article}>
        {c.sections.map((section) => (
          <section key={section.heading} className={styles.section}>
            <h2>{section.heading}</h2>
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </section>
        ))}
      </div>

      <section className={styles.faqBlock} aria-labelledby="seo-intent-faq">
        <h2 id="seo-intent-faq">
          {locale === 'fr' ? 'Questions fréquentes' : 'Frequently asked questions'}
        </h2>
        <dl className={styles.faqList}>
          {c.faqs.map((item) => (
            <div key={item.q} className={styles.faqItem}>
              <dt>{item.q}</dt>
              <dd>{item.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <aside
        className={styles.related}
        aria-label={locale === 'fr' ? 'À lire aussi' : 'Related guides'}
      >
        <h2>{locale === 'fr' ? 'À lire aussi' : 'Related guides'}</h2>
        <div className={styles.relatedGrid}>
          {c.related.map((relatedIntent) => {
            const relatedCopy = COPY[relatedIntent][locale];
            return (
              <a
                key={relatedIntent}
                href={pageHref(relatedIntent, locale)}
                className={styles.relatedCard}
              >
                <strong>{relatedCopy.title}</strong>
                <span>{locale === 'fr' ? 'Lire le guide' : 'Read the guide'} →</span>
              </a>
            );
          })}
        </div>
      </aside>
    </PageShell>
  );
}
